<template>
  <div class="post-card">
    <div class="post-header">
      <div class="post-author">
        <a-avatar
          v-if="isTeacherPost && teacherPhotoUrl"
          :size="46"
          class="post-avatar"
          :src="teacherPhotoUrl"
        />
        <a-avatar v-else-if="isTeacherPost" :size="46" class="post-avatar" icon="user" />
        <a-avatar v-else-if="!!logo" :size="46" class="post-avatar" :src="logo" />
        <a-avatar v-else :size="46" class="post-avatar" icon="user" />
        <div class="post-author-meta">
          <h5 class="post-title">{{ item.title }}</h5>
          <div class="post-subline">
            <span class="post-date" v-if="dateAbsolute">
              <a-icon type="calendar" />
              {{ dateAbsolute }}
            </span>
            <span class="post-meta-dot" v-if="dateAbsolute && dateRelative">·</span>
            <span class="post-relative" v-if="dateRelative">
              {{ dateRelative }}
            </span>
          </div>
        </div>
      </div>
      <div class="d-flex item-center post-header-right">
        <span
          v-if="enableValidation && (item.validationStatus === 'pending' || item.validationStatus === 'rejected')"
          class="status-pill"
          :class="item.validationStatus"
        >
          <a-icon :type="item.validationStatus === 'pending' ? 'clock-circle' : 'close-circle'" />
          {{ item.validationStatus === 'pending' ? $t("actualite.pendingValidation") : $t("actualite.rejected") }}
        </span>
        <a-dropdown :trigger="['click']">
          <button class="post-options-btn" @click.stop aria-label="options">
            <a-icon type="more" />
          </button>
          <a-menu slot="overlay" class="actions-dropdown-menu">
            <a-menu-item key="view" @click="() => $router.push(detailPath + item._id)">
              <a-icon type="eye" />
              {{ $t("action.consulter") }}
            </a-menu-item>
            <a-menu-item key="edit" @click="$emit('edit', item)">
              <a-icon type="edit" />
              {{ $t("action.modifier") }}
            </a-menu-item>
            <template v-if="enableValidation && item.validationStatus === 'pending'">
              <a-menu-divider />
              <a-menu-item key="approve" class="action-approve" @click="$emit('approve', item._id)">
                <a-icon type="check-circle" />
                {{ $t("actualite.approve") }}
              </a-menu-item>
              <a-menu-item key="reject" class="action-reject" @click="$emit('reject', item._id)">
                <a-icon type="close-circle" />
                {{ $t("actualite.reject") }}
              </a-menu-item>
            </template>
            <a-menu-divider />
            <a-menu-item key="delete" class="action-delete" @click="$emit('delete', item._id)">
              <a-icon type="delete" />
              {{ $t("action.supprimer") }}
            </a-menu-item>
          </a-menu>
        </a-dropdown>
      </div>
    </div>

    <div class="post-tags-row" v-if="showTagsRow">
      <slot name="extra-tags"></slot>
      <a-tooltip
        v-if="isTeacherPost && teacherFullName"
        :title="$t('actualite.createdByTeacher')"
      >
        <span class="teacher-chip">
          <a-icon type="user" />
          {{ teacherFullName }}
        </span>
      </a-tooltip>
      <span v-if="categoryLabel" class="category-badge" :class="categoryBadgeClass">
        <a-icon type="tag" />
        {{ categoryLabel }}
      </span>
      <span
        v-for="target in targets"
        :key="target.key"
        class="target-chip"
      >
        <a-icon :type="target.icon" />
        {{ target.label }}
      </span>
    </div>

    <div class="post-content">
      <div
        class="post-text"
        :class="{ expanded: expanded || !isLongText }"
        v-html="item.desc"
      ></div>
      <button
        v-if="isLongText"
        class="see-more-btn"
        @click.stop="expanded = !expanded"
      >
        {{ expanded ? $t("actualite.seeLess") : $t("actualite.seeMore") }}
      </button>
    </div>

    <div v-if="item.videos && item.videos.length > 0" class="videos-section">
      <div
        v-for="(video, videoIndex) in item.videos"
        :key="videoIndex"
        class="video-item mb-3"
      >
        <video-player
          :hls-url="getHlsUrl(video.hlsMasterKey)"
          :thumbnail-url="getThumbnailUrl(video.thumbnailKey)"
          :status="video.status"
          :resources-url="settings.resourcesUrl"
          :renditions="video.renditions || []"
          :original-key="video.originalKey"
          width="100%"
          height="400px"
        />
      </div>
    </div>

    <div class="post-media" :class="mediaGridClass" v-if="mediaPhotos.length">
      <div
        v-for="(file, i) in mediaPhotos.slice(0, 3)"
        :key="i"
        class="media-item"
      >
        <div class="media-item-inner pointer" @click.stop="$emit('show-image', mediaPhotos, i)">
          <template v-if="isImageFile(file)">
            <img
              :src="settings.resourcesUrl.replace('FILE_PATH', file)"
              alt="Image"
            />
          </template>
          <template v-else>
            <div class="document-preview">
              <img
                :src="getDocumentTypeImage(file)"
                class="document-type-icon"
              />
              <div class="document-name">{{ getFileName(file) }}</div>
            </div>
          </template>
        </div>
      </div>
      <div class="media-item" v-if="mediaPhotos.length >= 4">
        <div
          class="media-item-inner media-more-overlay pointer"
          @click.stop="showRemainingFiles(mediaPhotos)"
        >
          {{ "+" + (mediaPhotos.length - 3).toString() }}
        </div>
      </div>
    </div>

    <div
      class="post-stats"
      v-if="item.total_likes || (item.comments && item.comments.length) || showSeenStat"
    >
      <span class="likes-pill" v-if="item.total_likes" @click="showWhoLiked">
        <span class="heart-badge"><a-icon type="heart" theme="filled" /></span>
        {{ item.total_likes }}
      </span>
      <span v-else></span>
      <div class="stats-right">
        <a-tooltip
          v-if="item.comments && item.comments.length"
          :title="$t('actualite.commentsCountLabel', { count: item.comments.length })"
        >
          <span class="comments-pill" @click="$emit('toggle-comment', index)">
            <a-icon type="message" />
            {{ item.comments.length }}
          </span>
        </a-tooltip>
        <a-tooltip v-if="showSeenStat" :title="$t('actualite.seenNotSeenParentsLabel')">
          <span class="seen-pill" @click="openSeenStatus">
            <a-icon type="eye" />
            {{ item.total_seen || 0 }}
          </span>
        </a-tooltip>
      </div>
    </div>

    <div class="post-actions-bar">
      <button
        class="action-btn"
        :class="{ liked: item.liked }"
        @click="item.liked ? $emit('unlike', item._id, index) : $emit('like', item._id, index)"
      >
        <span class="icon-wrap">
          <a-icon type="heart" :theme="item.liked ? 'filled' : 'outlined'" />
        </span>
        {{ item.liked ? $t("actualite.liked") : $t("actualite.like") }}
      </button>
      <button class="action-btn" @click="$emit('toggle-comment', index)">
        <span class="icon-wrap">
          <a-icon type="message" />
        </span>
        {{ $t("actualite.commentBtn") }}
      </button>
    </div>

    <transition name="fade-slide">
      <div class="post-comments-wrap" v-if="item.showComments">
        <div class="post-comments" v-if="item.comments && item.comments.length">
          <div
            v-for="(comment, i) in item.comments"
            :key="comment._id || i"
            class="comment-bubble-row"
            v-show="item.comments[i].deleted !== true"
          >
            <a-avatar :size="32" v-if="comment.type === 'Admin'" :src="logo" alt="image" />
            <a-avatar
              :size="32"
              v-else
              :src="settings.resourcesUrl.replace('FILE_PATH', comment.photo)"
              alt="image"
            />
            <div class="comment-content">
              <div class="comment-meta">
                <span class="comment-author">{{
                  comment.type === "Admin" ? schoolName : comment.fullName
                }}</span>
                <span class="comment-meta-dot">·</span>
                <span class="comment-date" v-if="commentDateAbsolute(comment)">{{ commentDateAbsolute(comment) }}</span>
                <span class="comment-time" v-if="commentDateRelative(comment)">({{ commentDateRelative(comment) }})</span>
                <a-tooltip :title="$t('action.supprimer')">
                  <span
                    class="comment-delete"
                    @click="$emit('delete-comment', comment._id, index)"
                  >
                    <a-icon type="delete" />
                  </span>
                </a-tooltip>
              </div>
              <p class="comment-text">{{ comment.desc }}</p>
            </div>
          </div>
        </div>
        <div v-else class="no-comments">{{ $t("actualite.noCommentsYet") }}</div>

        <div class="post-comment-input" v-if="item.commentsEnabled !== false">
          <a-avatar :size="34" v-if="logo" :src="logo" />
          <div class="comment-input-wrap">
            <div class="comment-input-pill">
              <a-textarea
                :placeholder="$t('actualite.writeCommentPlaceholder')"
                auto-size
                v-model="localCommentText"
                @keydown.enter.exact.prevent="handleSubmitComment"
              />
              <div class="comment-input-tools">
                <button
                  type="button"
                  class="pill-icon-btn emoji-btn"
                  :title="$t('actualite.emojiPickerLabel')"
                  @click.stop="showEmojis = !showEmojis"
                >
                  <a-icon type="smile" />
                </button>
                <div class="emoji-selector" v-if="showEmojis">
                  <ul class="emoji-list">
                    <li
                      v-for="(emoji, y) of emojis"
                      :key="y"
                      @click="appendEmoji(emoji)"
                    >
                      {{ emoji }}
                    </li>
                  </ul>
                </div>
              </div>
              <button
                type="button"
                class="pill-icon-btn send"
                :title="$t('actualite.sendCommentLabel')"
                @click="handleSubmitComment"
              >
                <a-icon type="enter" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Likes Modal -->
    <a-modal
      v-model="visibleLikes"
      :footer="null"
      :closable="false"
      :width="440"
      :bodyStyle="{ padding: 0 }"
      wrapClassName="fb-card-modal"
      centered
    >
      <div class="fb-modal-header">
        <span class="fb-modal-title">
          <span class="fb-modal-title-icon icon-likes">
            <a-icon type="heart" theme="filled" />
          </span>
          {{ $t("actualite.mentionJaimes") }}
        </span>
        <button class="fb-modal-close" @click="visibleLikes = false" aria-label="close">
          <a-icon type="close" />
        </button>
      </div>
      <div class="fb-modal-body">
        <div class="fb-modal-loading" v-if="loadingLikes">
          <a-skeleton avatar :paragraph="{ rows: 4 }" />
        </div>
        <div class="likes-list" v-else>
          <div v-for="(like, i) in likes" :key="i" class="like-row">
            <a-avatar
              v-if="like.type === 'Admin'"
              :size="38"
              :src="logo"
              alt="image"
            />
            <a-avatar
              v-else-if="like.type == 'Teacher'"
              :size="38"
              :src="
                settings.resourcesUrl.replace('FILE_PATH', like.teacher.photo)
              "
              alt="image"
            />
            <a-avatar
              v-else-if="like.type == 'Parent'"
              :size="38"
              :src="
                settings.resourcesUrl.replace(
                  'FILE_PATH',
                  (like.parentType && like.parent[like.parentType]) ||
                    like.photo
                    ? like.parent[like.parentType].photo || like.parent.photo
                    : ''
                )
              "
              alt="image"
            />
            <span class="like-name">
              {{ like.type == "Admin" ? schoolName : like.fullName }}
            </span>
            <span v-if="like.type == 'Teacher'" class="like-type-chip">
              <a-icon type="solution" />
            </span>
          </div>
        </div>
      </div>
    </a-modal>

    <!-- Seen Status Modal -->
    <a-modal
      v-model="visibleSeenStatus"
      :footer="null"
      :closable="false"
      :width="560"
      :bodyStyle="{ padding: 0 }"
      wrapClassName="fb-card-modal"
      centered
    >
      <div class="fb-modal-header">
        <span class="fb-modal-title">
          <span class="fb-modal-title-icon icon-seen">
            <a-icon type="eye" />
          </span>
          {{ $t("actualite.seenNotSeenParentsTitle") }}
        </span>
        <button class="fb-modal-close" @click="visibleSeenStatus = false" aria-label="close">
          <a-icon type="close" />
        </button>
      </div>
      <div class="fb-modal-body seen-status-body">
        <div class="fb-modal-loading" v-if="loadingSeenStatus">
          <a-skeleton avatar :paragraph="{ rows: 4 }" />
        </div>
        <template v-else>
          <div class="seen-tabs">
            <button
              class="seen-tab-btn"
              :class="{ active: activeSeenTab === 'seen' }"
              @click="activeSeenTab = 'seen'"
            >
              <a-icon type="check-circle" />
              {{ $t("actualite.seenCountLabel", { count: seenTotalCount }) }}
            </button>
            <button
              class="seen-tab-btn"
              :class="{ active: activeSeenTab === 'notSeen' }"
              @click="activeSeenTab = 'notSeen'"
            >
              <a-icon type="clock-circle" />
              {{ $t("actualite.notSeenCountLabel", { count: notSeenTotalCount }) }}
            </button>
          </div>

          <template v-if="activeSeenTab === 'seen'">
            <div class="seen-status-table-scroll">
              <table class="seen-status-table">
                <thead>
                  <tr>
                    <th>{{ $t("actualite.parentLabel") }}</th>
                    <th>{{ $t("actualite.seenAtLabel") }}</th>
                  </tr>
                </thead>
                <tbody v-if="seenParents.length > 0">
                  <tr v-for="(parent, i) in seenParents" :key="'seen-' + i">
                    <td>
                      <span class="parent-name">{{ formatParentLabel(parent) }}</span>
                      <span v-if="formatStudentsLabel(parent)" class="parent-students">
                        {{ formatStudentsLabel(parent) }}
                      </span>
                    </td>
                    <td class="seen-at-cell">{{ formatSeenDate(parent.seenAt) }}</td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="2" class="empty-cell">{{ $t("actualite.noParentSeenNews") }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="seenTotalPages > 1" class="seen-status-pagination">
              <a-pagination
                size="small"
                :current="seenCurrentPage"
                :total="seenTotalCount"
                :pageSize="seenPageSize"
                :showTotal="(total) => `${total}`"
                @change="handleSeenPageChange"
              />
            </div>
          </template>

          <template v-else>
            <div class="seen-status-table-scroll">
              <table class="seen-status-table">
                <thead>
                  <tr>
                    <th>{{ $t("actualite.parentLabel") }}</th>
                  </tr>
                </thead>
                <tbody v-if="notSeenParents.length > 0">
                  <tr v-for="(parent, i) in notSeenParents" :key="'not-seen-' + i">
                    <td>
                      <span class="parent-name">{{ formatParentLabel(parent) }}</span>
                      <span v-if="formatStudentsLabel(parent)" class="parent-students">
                        {{ formatStudentsLabel(parent) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td class="empty-cell">{{ $t("actualite.allParentsSeenNews") }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="notSeenTotalPages > 1" class="seen-status-pagination">
              <a-pagination
                size="small"
                :current="notSeenCurrentPage"
                :total="notSeenTotalCount"
                :pageSize="notSeenPageSize"
                :showTotal="(total) => `${total}`"
                @change="handleNotSeenPageChange"
              />
            </div>
          </template>
        </template>
      </div>
    </a-modal>

    <!-- Remaining Files Modal -->
    <a-modal
      v-model="showRemainingFilesModal"
      :footer="null"
      :closable="false"
      :width="640"
      :bodyStyle="{ padding: 0 }"
      wrapClassName="fb-card-modal"
      centered
    >
      <div class="fb-modal-header">
        <span class="fb-modal-title">
          <span class="fb-modal-title-icon icon-files">
            <a-icon type="picture" />
          </span>
          {{ $t("actualite.files") }}
          <span class="fb-modal-count">{{ remainingFiles.length }}</span>
        </span>
        <button class="fb-modal-close" @click="showRemainingFilesModal = false" aria-label="close">
          <a-icon type="close" />
        </button>
      </div>
      <div class="fb-modal-body">
        <div class="files-grid">
          <div
            v-for="(file, i) in remainingFiles"
            :key="i"
            class="file-cell pointer"
            @click="$emit('show-image', [file], 0)"
          >
            <template v-if="isImageFile(file)">
              <img
                :src="settings.resourcesUrl.replace('FILE_PATH', file)"
                alt="Image"
              />
            </template>
            <template v-else>
              <div class="document-preview">
                <img
                  :src="getDocumentTypeImage(file)"
                  class="document-type-icon"
                />
                <div class="document-name">{{ getFileName(file) }}</div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import moment from "moment";
import apiClient from "@/services/axios";
import VideoPlayer from "@/components/VideoPlayer.vue";

export default {
  name: "ActualityPostCard",
  components: { VideoPlayer },
  props: {
    item: { type: Object, required: true },
    index: { type: Number, required: true },
    logo: { type: String, default: null },
    schoolName: { type: String, default: "" },
    settings: { type: Object, required: true },
    emojis: { type: Array, default: () => [] },
    /** "actuality" | "activity-club" — controls likes/seen API paths */
    resourceType: { type: String, default: "actuality" },
    detailPath: { type: String, default: "/actuality/" },
    enableValidation: { type: Boolean, default: true },
    enableSeen: { type: Boolean, default: true },
  },
  data() {
    return {
      expanded: false,
      showEmojis: false,
      localCommentText: "",
      likes: [],
      visibleLikes: false,
      loadingLikes: true,
      visibleSeenStatus: false,
      loadingSeenStatus: false,
      seenParents: [],
      notSeenParents: [],
      activeSeenTab: "seen",
      seenCurrentPage: 1,
      seenPageSize: 50,
      seenTotalCount: 0,
      seenTotalPages: 1,
      notSeenCurrentPage: 1,
      notSeenPageSize: 50,
      notSeenTotalCount: 0,
      notSeenTotalPages: 1,
      showRemainingFilesModal: false,
      remainingFiles: [],
    };
  },
  computed: {
    isTeacherPost() {
      return this.item.createdBy === "teacher" && !!this.item.createdByTeacher;
    },
    teacherPhotoUrl() {
      const photo =
        this.item.createdByTeacher &&
        this.item.createdByTeacher.employee &&
        this.item.createdByTeacher.employee.photo;
      return photo ? this.settings.resourcesUrl.replace("FILE_PATH", photo) : null;
    },
    teacherFullName() {
      const emp = this.item.createdByTeacher && this.item.createdByTeacher.employee;
      if (!emp) return "";
      return [emp.firstName, emp.middleName, emp.lastName].filter(Boolean).join(" ");
    },
    mediaPhotos() {
      if (Array.isArray(this.item.photos) && this.item.photos.length) {
        return this.item.photos;
      }
      return [this.item.coverImage, ...(this.item.attachments || [])].filter(Boolean);
    },
    showTagsRow() {
      return (
        !!this.$slots["extra-tags"] ||
        !!this.categoryLabel ||
        this.targets.length > 0 ||
        (this.isTeacherPost && !!this.teacherFullName)
      );
    },
    showSeenStat() {
      return this.enableSeen && this.isParentTracked;
    },
    dateAbsolute() {
      return this.item.createdAt ? moment(this.item.createdAt).format("DD/MM/YYYY") : "";
    },
    dateRelative() {
      if (this.item.relativeTime) return this.relativeTimeLabel(this.item.relativeTime);
      return this.item.createdAt ? moment(this.item.createdAt).fromNow() : "";
    },
    categoryLabel() {
      if (!this.item.category || this.item.category === "actualite") return null;
      const map = {
        album: this.$t("actualite.album"),
        info: this.$t("actualite.categoryInfo"),
        important_info: this.$t("actualite.categoryImportantInfo"),
        urgent: this.$t("actualite.categoryUrgent"),
        avis_important: this.$t("actualite.categoryAvisImportant"),
        avis: this.$t("actualite.categoryAvis"),
        important: this.$t("actualite.categoryImportant"),
      };
      return map[this.item.category] || this.item.category;
    },
    categoryBadgeClass() {
      const known = ["album", "info", "important_info", "urgent", "avis_important", "avis", "important"];
      return known.includes(this.item.category) ? "cat-" + this.item.category : "cat-default";
    },
    targets() {
      if (!Array.isArray(this.item.target)) return [];
      const map = {
        all: { label: this.$t("actualite.tous"), icon: "global" },
        parents: { label: this.$t("actualite.parents"), icon: "team" },
        students: { label: this.$t("actualite.students"), icon: "smile" },
        teacher: { label: this.$t("actualite.teachers"), icon: "solution" },
        administration: { label: this.$t("actualite.administration"), icon: "bank" },
      };
      if (this.item.target.includes("all")) {
        return [{ key: "all", ...map.all }];
      }
      return this.item.target
        .filter((t) => map[t])
        .map((t) => ({ key: t, ...map[t] }));
    },
    isLongText() {
      return this.stripHtml(this.item.desc).length > 260;
    },
    mediaGridClass() {
      const len = this.mediaPhotos.length || 0;
      return "grid-" + Math.min(len, 4);
    },
    isParentTracked() {
      return (
        Array.isArray(this.item?.target) &&
        this.item.target.some((t) => t === "parents" || t === "all")
      );
    },
  },
  methods: {
    likesUrl() {
      return this.resourceType === "activity-club"
        ? `/activity-club/${this.item._id}/likes`
        : `/actuality/likes/${this.item._id}`;
    },
    seenStatusUrl() {
      return this.resourceType === "activity-club"
        ? `/activity-club/${this.item._id}/seen-status`
        : `/actuality/${this.item._id}/seen-status`;
    },
    async showWhoLiked() {
      this.loadingLikes = true;
      this.visibleLikes = true;
      await apiClient
        .get(this.likesUrl())
        .then(({ data }) => {
          this.likes = data.likes;
          this.loadingLikes = false;
        })
        .catch((e) => console.log(e));
    },

    showRemainingFiles(files) {
      this.remainingFiles = files.slice(3);
      this.showRemainingFilesModal = true;
    },

    formatSeenDate(date) {
      if (!date) return "";
      return moment(date).format("DD/MM/YYYY HH:mm");
    },

    formatParentLabel(parent) {
      if (!parent) return "";
      return parent.parentType
        ? `${parent.parentName} (${parent.parentType})`
        : parent.parentName;
    },

    formatStudentsLabel(parent) {
      if (!parent || !Array.isArray(parent.students) || parent.students.length === 0) return "";
      const names = parent.students
        .map((s) => `${s.firstName || ""} ${s.lastName || ""}`.trim())
        .filter(Boolean);
      return names.length > 0 ? `(${names.join(", ")})` : "";
    },

    async _fetchSeenStatusPage({ seenPage, notSeenPage } = {}) {
      const params = {
        seenPage: seenPage || this.seenCurrentPage,
        seenLimit: this.seenPageSize,
        notSeenPage: notSeenPage || this.notSeenCurrentPage,
        notSeenLimit: this.notSeenPageSize,
      };
      const { data } = await apiClient.get(this.seenStatusUrl(), { params });
      this.seenTotalCount = data.seenCount || 0;
      this.notSeenTotalCount = data.notSeenCount || 0;
      this.seenTotalPages = (data.pagination && data.pagination.seenTotalPages) || 1;
      this.notSeenTotalPages = (data.pagination && data.pagination.notSeenTotalPages) || 1;
      this.seenParents = data.seenParents || [];
      this.notSeenParents = data.notSeenParents || [];
    },

    async _fetchWithRetry(fn, retries = 2, delayMs = 1500) {
      for (let attempt = 0; attempt <= retries; attempt++) {
        try {
          return await fn();
        } catch (err) {
          if (attempt === retries) throw err;
          await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
        }
      }
    },

    async openSeenStatus() {
      this.visibleSeenStatus = true;
      this.loadingSeenStatus = true;
      this.activeSeenTab = "seen";
      this.seenParents = [];
      this.notSeenParents = [];
      this.seenCurrentPage = 1;
      this.notSeenCurrentPage = 1;
      try {
        await this._fetchWithRetry(() => this._fetchSeenStatusPage());
      } catch (error) {
        this.$message.error(this.$t("error.unexpectedError"));
      } finally {
        this.loadingSeenStatus = false;
      }
    },

    async handleSeenPageChange(page) {
      this.seenCurrentPage = page;
      this.loadingSeenStatus = true;
      try {
        await this._fetchWithRetry(() => this._fetchSeenStatusPage({ seenPage: page }));
      } catch (error) {
        this.$message.error(this.$t("error.unexpectedError"));
      } finally {
        this.loadingSeenStatus = false;
      }
    },

    async handleNotSeenPageChange(page) {
      this.notSeenCurrentPage = page;
      this.loadingSeenStatus = true;
      try {
        await this._fetchWithRetry(() => this._fetchSeenStatusPage({ notSeenPage: page }));
      } catch (error) {
        this.$message.error(this.$t("error.unexpectedError"));
      } finally {
        this.loadingSeenStatus = false;
      }
    },

    stripHtml(html) {
      if (!html) return "";
      return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    },
    relativeTimeLabel(rt) {
      if (!rt || rt.unit == null) return "";
      const key = rt.count === 1 ? `time.${rt.unit}Ago` : `time.${rt.unit}sAgo`;
      return this.$te(key) ? this.$t(key, { count: rt.count }) : `${rt.count} ${rt.unit}${rt.count !== 1 ? "s" : ""} ago`;
    },
    commentDateAbsolute(comment) {
      return comment.createdAt ? moment(comment.createdAt).format("DD/MM/YYYY") : "";
    },
    commentDateRelative(comment) {
      if (comment.relativeTime) return this.relativeTimeLabel(comment.relativeTime);
      return comment.createdAt ? moment(comment.createdAt).fromNow() : "";
    },
    appendEmoji(emoji) {
      this.localCommentText = (this.localCommentText || "") + emoji;
      this.showEmojis = false;
    },
    handleSubmitComment() {
      const text = this.localCommentText;
      if (!text || !text.trim()) return;
      this.$emit("submit-comment", text, this.item._id);
      this.localCommentText = "";
    },
    isImageFile(file) {
      const ext = file.split(".").pop().toLowerCase();
      return ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
    },
    getFileName(file) {
      const fullName = file.split("/").pop();
      const separatorIndex = fullName.indexOf("--");
      return separatorIndex !== -1
        ? fullName.substring(separatorIndex + 2)
        : fullName;
    },
    getDocumentTypeImage(file) {
      const ext = file.split(".").pop().toLowerCase();
      const imageMap = {
        pdf: "/resources/images/pdf-icon.png",
        doc: "/resources/images/word-icon.png",
        docx: "/resources/images/word-icon.png",
      };
      return imageMap[ext];
    },
    getHlsUrl(hlsMasterKey) {
      if (!hlsMasterKey) return null;
      return this.settings.resourcesUrl.replace("FILE_PATH", hlsMasterKey);
    },
    getThumbnailUrl(thumbnailKey) {
      if (!thumbnailKey) return null;
      return this.settings.resourcesUrl.replace("FILE_PATH", thumbnailKey);
    },
  },
};
</script>

<style scoped lang="scss">
$fb-blue: #4b7cf3;
$fb-blue-light: #eef4ff;
$fb-bg: #f0f2f5;
$fb-text: #1c1e21;
$fb-muted: #65676b;
$fb-border: #eef0f3;
$fb-like: #e0245e;

.pointer {
  cursor: pointer;
}

.item-center {
  align-items: center;
}

.post-card {
  background: #fff;
  border: 1px solid #e4e6eb;
  border-radius: 12px;
  margin-bottom: 16px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.03);
  transition: transform 0.2s ease, border-color 0.2s ease;
  overflow: hidden;

  :deep(.anticon) {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    line-height: 1;
    vertical-align: middle;
  }

  &:hover {
    transform: translateY(-2px);
    border-color: #d0d3d9;
  }
}

.post-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 14px 18px 10px;
}

.post-author {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
}

.post-avatar {
  flex-shrink: 0;
}

.post-author-meta {
  min-width: 0;
}

.post-title {
  margin: 0;
  font-size: 15.5px;
  font-weight: 700;
  color: $fb-text;
  line-height: 1.3;
}

.post-header-right {
  gap: 8px;
  flex-shrink: 0;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  height: 22px;
  padding: 0 10px;
  border-radius: 20px;
  line-height: 1;
  white-space: nowrap;

  :deep(.anticon) {
    font-size: 11px;
    line-height: 1;
  }

  &.pending {
    background: #fff7e6;
    color: #d48806;
    border: 1px solid #ffe7ba;
  }

  &.rejected {
    background: #fff1f0;
    color: #cf1322;
    border: 1px solid #ffccc7;
  }
}

.post-tags-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 18px 8px;
}

.post-subline {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;

  > * {
    display: inline-flex;
    align-items: center;
  }
}

.post-meta-dot {
  color: #bcc0c4;
  font-size: 12px;
  line-height: 1;
  user-select: none;
}

.post-date {
  font-size: 12.5px;
  font-weight: 500;
  color: $fb-muted;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  line-height: 1;

  :deep(.anticon) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12.5px;
    line-height: 1;
    color: $fb-muted;

    svg {
      display: block;
    }
  }
}

.post-relative {
  font-size: 12px;
  font-weight: 500;
  font-style: italic;
  color: #8a8d91;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.teacher-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  height: 22px;
  padding: 0 10px;
  border-radius: 20px;
  background: #f3e8ff;
  color: #9333ea;
  line-height: 1;
  max-width: 100%;

  :deep(.anticon) {
    font-size: 11px;
    line-height: 1;
  }
}

.category-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  height: 22px;
  padding: 0 10px;
  border-radius: 20px;
  background: $fb-blue-light;
  color: $fb-blue;
  line-height: 1;

  :deep(.anticon) {
    font-size: 10px;
    line-height: 1;
  }

  &.cat-album {
    background: #f3e8ff;
    color: #9333ea;
  }
  &.cat-urgent {
    background: #fee2e2;
    color: #dc2626;
  }
  &.cat-important,
  &.cat-important_info,
  &.cat-avis_important {
    background: #fff4e5;
    color: #d97706;
  }
  &.cat-avis {
    background: #e6fbf3;
    color: #0f9d75;
  }
  &.cat-info {
    background: $fb-blue-light;
    color: $fb-blue;
  }
}

.target-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  height: 22px;
  padding: 0 10px;
  border-radius: 20px;
  background: #f0f2f5;
  color: #4b4f56;
  border: 1px solid #e4e6eb;
  line-height: 1;

  :deep(.anticon) {
    font-size: 11px;
    line-height: 1;
    color: #65676b;
  }
}

.post-options-btn {
  border: none;
  background: transparent;
  color: $fb-muted;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  flex-shrink: 0;
  transition: background 0.15s ease;

  :deep(.anticon) {
    line-height: 1;
  }

  &:hover {
    background: $fb-bg;
  }
}

.post-content {
  padding: 0 18px 6px;
}

.post-text {
  font-size: 14.5px;
  line-height: 1.5;
  color: $fb-text;
  max-height: 4.5em;
  overflow: hidden;
  position: relative;
  transition: max-height 0.25s ease;

  &.expanded {
    max-height: none;
  }
}

.see-more-btn {
  border: none;
  background: transparent;
  color: $fb-muted;
  font-weight: 600;
  font-size: 13px;
  padding: 4px 0 2px;
  cursor: pointer;

  &:hover {
    color: $fb-blue;
    text-decoration: underline;
  }
}

.videos-section {
  padding: 12px 18px;
  border-top: 1px solid $fb-border;
  border-bottom: 1px solid $fb-border;
  margin: 4px 0;
}

.video-item {
  width: 100%;
  max-width: 100%;
}

.post-media {
  display: grid;
  gap: 3px;
  margin: 0 0 4px;

  .media-item-inner {
    width: 100%;
    height: 100%;
  }

  &.grid-1 .media-item-inner {
    height: 380px;
  }
  &.grid-2 {
    grid-template-columns: 1fr 1fr;
    .media-item-inner {
      height: 240px;
    }
  }
  &.grid-3 {
    grid-template-columns: 2fr 1fr;
    grid-template-rows: 1fr 1fr;
    .media-item:first-child {
      grid-row: 1 / 3;
    }
    .media-item-inner {
      height: 100%;
      min-height: 150px;
    }
  }
  &.grid-4 {
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 1fr 1fr;
    .media-item-inner {
      height: 150px;
    }
  }
}

.media-item {
  position: relative;
}

.media-item-inner {
  position: relative;
  overflow: hidden;
  background: $fb-bg;
  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(0.94);
  }

  img {
    &:not(.document-type-icon) {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
    }
  }
}

.media-more-overlay {
  background: rgba(0, 0, 0, 0.55) !important;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  font-weight: 700;
}

.document-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 12px;
  background: #f7f8fa;

  .document-type-icon {
    width: 48px;
    height: 48px;
    object-fit: contain;
  }

  .document-name {
    margin-top: 8px;
    font-size: 12px;
    color: $fb-muted;
    text-align: center;
    word-break: break-word;
  }
}

.post-stats {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 18px;
  font-size: 13px;
  color: $fb-muted;
}

.likes-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  line-height: 1.25;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.heart-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 19px;
  height: 19px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f5576c, #ff8a9e);
  color: #fff;
  font-size: 10px;
  line-height: 1;
  flex-shrink: 0;

  :deep(.anticon) {
    font-size: 10px;
    line-height: 1;
  }
}

.comments-pill,
.seen-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  color: $fb-muted;
  font-size: 13px;
  line-height: 1.25;

  :deep(.anticon) {
    font-size: 15px;
    color: #65676b;
    line-height: 1;
  }

  &:hover {
    text-decoration: underline;
    color: $fb-text;
  }
}

.stats-right {
  display: flex;
  align-items: center;
  gap: 14px;
}

.post-actions-bar {
  display: flex;
  border-top: 1px solid $fb-border;
  margin: 0 18px;
  padding: 4px 0;
}

.action-btn {
  flex: 1;
  border: none;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 0;
  margin: 4px 2px;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.25;
  color: $fb-muted;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.1s ease;

  &:hover {
    background: $fb-bg;
  }

  &:active {
    transform: scale(0.97);
  }

  .icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    font-size: 16px;

    :deep(.anticon) {
      font-size: 16px;
      line-height: 1;
    }
  }

  &.liked {
    color: $fb-like;

    .icon-wrap {
      animation: heartPop 0.4s ease;
    }
  }
}

@keyframes heartPop {
  0% {
    transform: scale(1);
  }
  30% {
    transform: scale(1.35);
  }
  60% {
    transform: scale(0.92);
  }
  100% {
    transform: scale(1);
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.post-comments-wrap {
  padding: 12px 18px 16px;
  border-top: 1px solid $fb-border;
  background: transparent;
}

.post-comments {
  max-height: 280px;
  overflow-y: auto;
  overflow-x: hidden;
  margin-bottom: 10px;
  padding-right: 4px;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c9ccd1;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
}

.no-comments {
  text-align: center;
  color: $fb-muted;
  font-size: 13px;
  padding: 12px 0;
}

.comment-bubble-row {
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f2f5;

  &:last-child {
    border-bottom: none;
    padding-bottom: 4px;
  }
}

.comment-content {
  flex: 1;
  min-width: 0;
  background: transparent;
}

.comment-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 2px;
}

.comment-author {
  font-weight: 600;
  font-size: 13px;
  color: $fb-text;
  text-transform: capitalize;
}

.comment-meta-dot {
  color: #bcc0c4;
  font-size: 12px;
  user-select: none;
}

.comment-delete {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 13px;
  color: $fb-like;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease, background 0.15s ease;

  &:hover {
    background: #fdecef;
  }
}

.comment-bubble-row:hover .comment-delete {
  opacity: 1;
}

.comment-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.45;
  color: $fb-text;
  word-break: break-word;
  white-space: pre-wrap;
}

.comment-date {
  font-size: 12px;
  font-weight: 500;
  font-style: normal;
  color: $fb-muted;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.comment-time {
  font-size: 12px;
  font-weight: 500;
  font-style: italic;
  color: #8a8d91;
  line-height: 1;
  display: inline-flex;
  align-items: center;
}

.post-comment-input {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #f0f2f5;
}

.comment-input-wrap {
  flex: 1;
  min-width: 0;
}

.comment-input-pill {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 2px;
  background: $fb-bg;
  border-radius: 20px;
  padding: 3px 6px 3px 14px;
  transition: background 0.15s ease;

  &:focus-within {
    background: $fb-blue-light;
  }

  textarea {
    border: none;
    background: transparent;
    resize: none;
    box-shadow: none;
    padding: 4px 0;
    min-height: 22px;
    max-height: 90px;
    line-height: 1.4;
    font-size: 13.5px;
  }
}

.comment-input-tools {
  position: relative;
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.pill-icon-btn {
  border: none;
  background: transparent;
  color: $fb-muted;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  line-height: 1;
  transition: background 0.15s ease, color 0.15s ease;

  :deep(.anticon) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    line-height: 1;

    svg {
      display: block;
    }
  }

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  &.emoji-btn {
    color: #e8a317;
  }

  &.emoji-btn:hover {
    color: #d69410;
    background: rgba(232, 163, 23, 0.12);
  }

  &.send {
    color: $fb-blue;
  }
}

.emoji-selector {
  background-color: white;
  border-radius: 1rem;
  cursor: default;
  display: block;
  padding: 10px 0;
  border: 1px solid #eee;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  width: 280px;
  height: 300px;
  z-index: 10;
  overflow-y: auto;
  overflow-x: hidden;
}

.emoji-selector ul.emoji-list {
  height: 340px;
  background-color: white;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  padding: 0 1rem 1rem;
  border-radius: 0;
  list-style: none;
}

.emoji-selector ul.emoji-list li {
  height: 35px;
  width: 35px;
  border-radius: 0.25rem;
  font-size: 1.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.emoji-selector ul.emoji-list li:hover {
  background-color: #eeee;
}

/* ---------- FB-style modals (likes / seen / files) ---------- */
.fb-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid $fb-border;
}

.fb-modal-title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 15.5px;
  font-weight: 700;
  color: $fb-text;
  line-height: 1.25;
  min-width: 0;
}

.fb-modal-title-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;

  :deep(.anticon) {
    font-size: 14px;
    line-height: 1;
  }

  &.icon-likes {
    background: linear-gradient(135deg, #f5576c, #ff8a9e);
    color: #fff;
  }

  &.icon-seen {
    background: $fb-blue-light;
    color: $fb-blue;
  }

  &.icon-files {
    background: #e6fbf3;
    color: #0f9d75;
  }
}

.fb-modal-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 8px;
  border-radius: 20px;
  background: $fb-bg;
  color: $fb-muted;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

.fb-modal-close {
  border: none;
  background: $fb-bg;
  color: $fb-muted;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  font-size: 14px;
  line-height: 1;
  transition: background 0.15s ease, transform 0.15s ease;

  :deep(.anticon) {
    line-height: 1;
  }

  &:hover {
    background: #e4e6eb;
    transform: scale(1.05);
  }
}

.fb-modal-body {
  padding: 8px 10px 14px;
}

.fb-modal-loading {
  padding: 16px 12px;
}

/* Likes list */
.likes-list {
  max-height: 340px;
  overflow-y: auto;
  padding: 4px 0;
}

.like-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 10px;
  transition: background 0.15s ease;

  &:hover {
    background: $fb-bg;
  }
}

.like-name {
  font-size: 14px;
  font-weight: 600;
  color: $fb-text;
  text-transform: capitalize;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.like-type-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f3e8ff;
  color: #9333ea;
  font-size: 11px;
  line-height: 1;
  flex-shrink: 0;

  :deep(.anticon) {
    line-height: 1;
  }
}

/* Seen status */
.seen-status-body {
  min-height: 420px;
  padding: 12px 14px 14px;
}

.seen-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.seen-tab-btn {
  flex: 1;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 9px 12px;
  border-radius: 20px;
  background: $fb-bg;
  color: $fb-muted;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  :deep(.anticon) {
    font-size: 13px;
    line-height: 1;
  }

  &:hover {
    background: #e4e6eb;
  }

  &.active {
    background: $fb-blue-light;
    color: $fb-blue;
  }
}

.seen-status-table-scroll {
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid $fb-border;
  border-radius: 10px;
}

.seen-status-table {
  width: 100%;
  border-collapse: collapse;

  th {
    position: sticky;
    top: 0;
    background: #f8f9fb;
    font-size: 11.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: $fb-muted;
    padding: 9px 12px;
    text-align: left;
    border-bottom: 1px solid $fb-border;
  }

  td {
    padding: 9px 12px;
    border-bottom: 1px solid #f5f6f8;
    font-size: 13.5px;
    color: $fb-text;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr {
    transition: background 0.15s ease;

    &:hover {
      background: #fafbfc;
    }
  }
}

.parent-name {
  text-transform: capitalize;
  font-weight: 600;
}

.parent-students {
  margin-left: 6px;
  font-size: 12px;
  color: $fb-muted;
  text-transform: capitalize;
}

.seen-at-cell {
  color: $fb-muted;
  font-size: 12.5px;
  white-space: nowrap;
}

.empty-cell {
  text-align: center;
  color: $fb-muted;
  padding: 24px 12px !important;
}

.seen-status-pagination {
  display: flex;
  justify-content: center;
  padding: 12px 0 0;
}

/* Remaining files grid */
.files-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
  padding: 6px 4px 2px;
  max-height: 440px;
  overflow-y: auto;
}

.file-cell {
  position: relative;
  height: 150px;
  border-radius: 10px;
  overflow: hidden;
  background: $fb-bg;
  transition: filter 0.2s ease, transform 0.15s ease;

  &:hover {
    filter: brightness(0.94);
    transform: translateY(-2px);
  }

  img {
    &:not(.document-type-icon) {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      object-fit: contain;
    }
  }
}
</style>

<style lang="scss">
.fb-card-modal .ant-modal-content {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
}

/* ---------- Post options dropdown (FB style) ---------- */
.actions-dropdown-menu.ant-dropdown-menu {
  border-radius: 12px;
  padding: 6px;
  min-width: 215px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15), 0 2px 6px rgba(0, 0, 0, 0.08);
}

.actions-dropdown-menu .ant-dropdown-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 8px;
  font-size: 13.5px;
  font-weight: 600;
  color: #1c1e21;
  line-height: 1.3;
  transition: background 0.15s ease;
}

.actions-dropdown-menu .ant-dropdown-menu-item .anticon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #f0f2f5;
  color: #65676b;
  font-size: 14px;
  line-height: 1;
  margin-right: 0;
  flex-shrink: 0;

  svg {
    display: block;
  }
}

.actions-dropdown-menu .ant-dropdown-menu-item:hover {
  background: #f0f2f5;
}

.actions-dropdown-menu .ant-dropdown-menu-item-divider {
  margin: 5px 8px;
  background: #eef0f3;
}

.actions-dropdown-menu .action-approve {
  color: #31a24c;
}
.actions-dropdown-menu .action-approve .anticon {
  background: #e6f7ec;
  color: #31a24c;
}
.actions-dropdown-menu .action-approve:hover {
  background: #e6f7ec;
}

.actions-dropdown-menu .action-reject {
  color: #d48806;
}
.actions-dropdown-menu .action-reject .anticon {
  background: #fff7e6;
  color: #d48806;
}
.actions-dropdown-menu .action-reject:hover {
  background: #fff7e6;
}

.actions-dropdown-menu .action-delete {
  color: #e0245e;
}
.actions-dropdown-menu .action-delete .anticon {
  background: #fdecef;
  color: #e0245e;
}
.actions-dropdown-menu .action-delete:hover {
  background: #fdecef;
}
</style>
